<template>
    <div >

        <v-form ref="form" v-model="valid_form" lazy-validation>
            <v-dialog
                v-model="openDialog"
                :fullscreen="fullscreen"
                hide-overlay
                transition="dialog-bottom-transition"
                width="auto"
                @click:outside="close"
            >

              
              <v-card class="mx-auto">
                <!-- ── Toolbar ─────────────────────────────────── -->
                <v-toolbar dark color="primary">
                  <v-toolbar-title style="font-size:1rem">{{ modalTitle }}</v-toolbar-title>
                  <v-spacer></v-spacer>
                  <v-btn icon dark @click="close">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-toolbar>

                <v-card-text class="pt-4">
                  <v-container grid-list-md>

                    <!-- ══════════════════════════════════════════
                         CHLA — names_with_options
                         Buscador individual + tarjeta por persona
                    ══════════════════════════════════════════ -->
                    <template v-if="responseType === 'names_with_options'">

                      <!-- Buscador (agrega personas una a una) -->
                      <v-autocomplete
                        v-model="personToAdd"
                        :items="availableEmployees"
                        item-text="username"
                        item-value="id"
                        :label="$t('solar_block.search_placeholder')"
                        :no-data-text="$t('solar_block.no_more_persons')"
                        dense
                        outlined
                        clearable
                        return-object
                        :filter="customFilter"
                        @change="onPersonSelected"
                        class="mb-2"
                      >
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>{{ item.username }}</v-list-item-title>
                            <v-list-item-subtitle class="text-caption">
                              {{ item.organization_area && item.organization_area.Organization_area_es }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>

                      <!-- Contador de personas seleccionadas -->
                      <div class="d-flex align-center mb-3">
                        <v-chip
                          small
                          :color="selected_persons.filter(Boolean).length ? 'primary' : 'grey lighten-2'"
                          class="mr-2"
                        >{{ selected_persons.filter(Boolean).length }}</v-chip>
                        <span class="text-caption grey--text">
                          {{ $t('solar_block.selected_persons_count') }}
                        </span>
                      </div>

                      <!-- Tarjeta por persona seleccionada -->
                      <v-card
                        v-for="person in selected_persons.filter(Boolean)"
                        :key="person.id"
                        outlined
                        class="mb-3 person-sel-card"
                      >
                        <!-- Encabezado: nombre + área + botón eliminar -->
                        <div class="person-sel-header d-flex align-center px-4 py-2">
                          <v-icon small color="primary" class="mr-2">mdi-account</v-icon>
                          <span class="font-weight-medium body-2">{{ person.username }}</span>
                          <span
                            v-if="person.organization_area"
                            class="ml-2 text-caption grey--text"
                          >· {{ person.organization_area.Organization_area_es }}</span>
                          <v-spacer />
                          <v-btn
                            icon x-small
                            @click="removePerson(person)"
                            :title="$t('solar_block.remove_person')"
                          >
                            <v-icon small color="grey darken-1">mdi-close</v-icon>
                          </v-btn>
                        </div>

                        <v-divider />

                        <!-- Checkboxes de razones con tooltip -->
                        <div class="px-4 py-3">
                          <div
                            v-for="opt in parsedOptions"
                            :key="opt.valor"
                            class="d-flex align-center mb-1"
                          >
                            <v-checkbox
                              v-model="selectedReasons[person.id]"
                              :value="opt.valor"
                              dense
                              hide-details
                              class="mt-0 pt-0 flex-shrink-0"
                            >
                              <template v-slot:label>
                                <span class="text-body-2">{{ opt.texto }}</span>
                              </template>
                            </v-checkbox>

                            <v-tooltip right max-width="280">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                  x-small
                                  class="ml-1 grey--text text--lighten-1"
                                  v-bind="attrs"
                                  v-on="on"
                                >mdi-help-circle-outline</v-icon>
                              </template>
                              <span>{{ getReasonDefinition(opt.valor) }}</span>
                            </v-tooltip>
                          </div>
                        </div>
                      </v-card>

                      <!-- Alerta de validación -->
                      <v-alert
                        v-if="validationError"
                        type="warning"
                        dense
                        text
                        class="mt-2 mb-0"
                      >{{ $t('solar_block.validation_required') }}</v-alert>

                    </template>

                    <!-- ══════════════════════════════════════════
                         Otros tipos: autocomplete múltiple clásico
                    ══════════════════════════════════════════ -->
                    <template v-else>
                      <v-col class="d-flex justify-space-around mb-6 align-end" cols="6">
                        <v-autocomplete
                          v-model="selected_persons"
                          :items="filteredEmployees"
                          item-text="username"
                          item-value="id"
                          :label="$t('active_source.persons')"
                          persistent-hint
                          small-chips
                          multiple
                          dense
                          clearable
                          deletable-chips
                          return-object
                        ></v-autocomplete>
                      </v-col>

                      <!-- Tabla names_only -->
                      <span v-if="responseType === 'names_only' && selected_persons.length > 0">
                        <v-simple-table fixed-header>
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th class="text-left">{{ $t('solar.person') }}</th>
                                <th class="text-left">{{ $t('solar.why_reason') }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="person in selected_persons" :key="person.id">
                                <td width="30%">{{ person.username }}</td>
                                <td>
                                  <v-textarea outlined class="mt-4"
                                    :name="`reason-${person.id}`"
                                    :placeholder="modalTitle"
                                    v-model="reasons[`${person.id}`]"
                                    rows="2"
                                  ></v-textarea>
                                </td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </span>

                      <!-- Tabla names_with_text -->
                      <span v-if="responseType === 'names_with_text' && selected_persons.length > 0">
                        <v-simple-table fixed-header>
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th class="text-left">{{ $t('solar.person') }}</th>
                                <th class="text-left">{{ $t('solar.reasons') }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="person in selected_persons" :key="person.id">
                                <td width="30%">{{ person.username }}</td>
                                <td>
                                  <v-textarea outlined class="mt-4"
                                    :name="`reason-${person.id}`"
                                    :label="$t('solar.describe_reasons')"
                                    v-model="reasons[`${person.id}`]"
                                    rows="3"
                                  ></v-textarea>
                                </td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </span>
                    </template>

                  </v-container>
                </v-card-text>

                <!-- ── Acciones ─────────────────────────────── -->
                <v-card-actions class="px-4 pb-4">
                  <v-btn text small color="grey darken-1" @click="selectNobody">
                    <v-icon left small>mdi-account-off-outline</v-icon>
                    {{ $t('solar_block.nobody') }}
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn color="blue" text @click="close">{{ $t('globals.cancel') }}</v-btn>
                  <v-btn color="success" @click.native="saveSelectedPersons">{{ $t('globals.save') }}</v-btn>
                </v-card-actions>

              </v-card>
            </v-dialog>
        </v-form>

  </div>

</template>

<script>
import { useMainStore } from '@/store/main';
import { mapStores} from 'pinia';
  
    export default {

      name: "SelectPersonsDialog",

      props:[
            'modalTitle',
            'showDialog',
            'questionID',
            'responseType',
            'defaultOptions',
            'maxSelections',
            'solarQuestion',
            'fullscreen'
            
        ],

      data: () => ({

            openDialog:    false,
            valid_form:    false,
            selected_persons: [],
            selected_area: '',
            reasons:       {},
            selectedReasons: {},  // { personId: [valor1, valor2] }  (names_with_options)
            response_options: [],
            showTextArea:  [],
            nobodySelected: false,

            // names_with_options: buscador individual
            personToAdd:     null,
            validationError: false,

        }),


      mounted(){

        // console.log('entra amounted!')

      },

      watch: {

        showDialog: {

            handler: function (val) {

              if (val) {
                this.selected_persons = [];
                this.reasons = {};
                this.selectedReasons = {};
                this.nobodySelected = false;
                // Restaurar selecciones previas inmediatamente: el watcher de defaultOptions
                // no se dispara si el prop no cambió entre cierre y reapertura.
                this.restoreFromOptions(this.defaultOptions);
              }
              this.openDialog = val;

            },

        },


        defaultOptions: {

            handler: function (val) {
              this.restoreFromOptions(val);
            },

        },


        selected_persons: {

          handler: function (val) {

            if (this.maxSelections != undefined && val.length > this.maxSelections) {
              alert('Máximo ' + this.maxSelections + ' nombres seleccionados permitidos!!');
              this.selected_persons.pop();
              return;
            }

            // Inicializar razones para personas recién seleccionadas (solo si aún no existen)
            if (val && val.length > 0) {
              val.filter(Boolean).forEach(person => {
                if (!(person.id in this.reasons)) {
                  this.$set(this.reasons, person.id, '');
                }
                if (!(person.id in this.selectedReasons)) {
                  this.$set(this.selectedReasons, person.id, []);
                }
              });
            }

          },

        },

        },

      computed: {

            // ...mapState(useMainStore, ["selected_cycle"]),
            ...mapStores(useMainStore),


            // Empleados que aún NO están en la lista seleccionada (para el buscador CHLA)
            availableEmployees() {
              const selectedIds = new Set(
                this.selected_persons.filter(Boolean).map(p => p.id)
              );
              return this.filteredEmployees.filter(emp => !selectedIds.has(emp.id));
            },

            filteredEmployees() {
                // Para CHLA (Bloque 1): el tipo names_with_options muestra toda la planta docente
                if (this.responseType === 'names_with_options') {
                  return this.mainStore.employees;
                }
                // Lógica original para otros tipos (datasets de estudiantes)
                if(this.solarQuestion && this.solarQuestion.id_question==1){
                  return this.mainStore.employees.filter(item => item.organization_area.Organization_area_es.slice(0, -1) === this.mainStore.logged_user.area_name.slice(0, -1));
                }
                else if (this.solarQuestion && this.solarQuestion.id_question==2){
                  return this.mainStore.employees.filter(item => item.id_organization_area === this.mainStore.logged_user.area_id);
                }
                else if (this.solarQuestion && this.solarQuestion.id_question==3){
                  return this.mainStore.employees.filter(item => item.organization_area.Organization_area_es.slice(0, -1) === this.mainStore.logged_user.area_name.slice(0, -1));
                }
                else if (this.solarQuestion && this.solarQuestion.id_question==4){
                  return this.mainStore.employees.filter(item => item.organization_area.Organization_area_es.slice(0, -1) === this.mainStore.logged_user.area_name.slice(0, -1));
                }
                else{
                  return this.mainStore.employees;
                }
            },

            options(){
              return JSON.parse(this.solarQuestion.question_possible_answers[`Question_possible_answers_${this.$i18n.locale}`]);
            },

            // Para names_with_options: parsea el JSON de opciones de la pregunta
            parsedOptions() {
              try {
                const raw = this.solarQuestion &&
                  this.solarQuestion.question_possible_answers &&
                  this.solarQuestion.question_possible_answers[`Question_possible_answers_${this.$i18n.locale}`];
                return raw ? JSON.parse(raw) : [];
              } catch {
                return [];
              }
            },
           
            
        },


      methods: {

        // ── Restaurar selecciones previas al abrir el diálogo ────────
        restoreFromOptions(val) {
          if (val === undefined || val === null) return;
          if (this.responseType === 'names_with_text') {
            for (let key in val) {
              const emp = this.filteredEmployees.find(person => person.id == key);
              if (emp && !this.selected_persons.some(p => p && p.id === emp.id)) {
                // Usar $set para que Vue 2 rastree la propiedad y v-model funcione correctamente
                this.$set(this.reasons, String(emp.id), val[key]);
                this.selected_persons.push(emp);
              }
            }
          } else if (this.responseType === 'names_with_options') {
            for (let key in val) {
              const emp = this.filteredEmployees.find(person => person.id == key);
              if (emp && !this.selected_persons.some(p => p && p.id === emp.id)) {
                this.selected_persons.push(emp);
                const checkboxValues = Array.isArray(val[key]) ? val[key] : [];
                this.$set(this.selectedReasons, emp.id, checkboxValues);
              }
            }
          } else if (this.responseType === 'names_only') {
            if (Array.isArray(val)) {
              val.forEach(id => {
                const emp = this.filteredEmployees.find(person => person.id == id);
                if (emp && !this.selected_persons.some(p => p && p.id === emp.id)) {
                  this.selected_persons.push(emp);
                }
              });
            } else {
              for (let key in val) {
                const emp = this.filteredEmployees.find(person => person.id == key);
                if (emp && !this.selected_persons.some(p => p && p.id === emp.id)) {
                  this.selected_persons.push(emp);
                  this.reasons[key] = val[key];
                }
              }
            }
          }
        },

        // ── Buscador individual CHLA ──────────────────────
        onPersonSelected(person) {
          if (!person) return;
          if (!this.selected_persons.some(p => p && p.id === person.id)) {
            this.selected_persons.push(person);
            this.$set(this.selectedReasons, person.id, []);
          }
          this.validationError = false;
          this.$nextTick(() => { this.personToAdd = null; });
        },

        removePerson(person) {
          const idx = this.selected_persons.findIndex(p => p && p.id === person.id);
          if (idx !== -1) this.selected_persons.splice(idx, 1);
          this.$delete(this.selectedReasons, person.id);
        },

        getReasonDefinition(valor) {
          const keyMap = { 0: 'trust', 1: 'accessibility', 2: 'knowledge', 3: 'hierarchy', 4: 'creativity' };
          const key = Object.prototype.hasOwnProperty.call(keyMap, valor) ? keyMap[valor] : String(valor);
          return this.$t(`solar_block.reason_defs.${key}`);
        },

        customFilter(item, queryText) {
          const name = (item.username || '').toLowerCase();
          const area = (item.organization_area && item.organization_area.Organization_area_es || '').toLowerCase();
          const q = (queryText || '').toLowerCase();
          return name.includes(q) || area.includes(q);
        },

        // ─────────────────────────────────────────────────
        selectNobody() {
            this.selected_persons = [];
            this.reasons = {};
            this.selectedReasons = {};
            this.nobodySelected = true;
            this.$emit('personsSelected', { selected_persons: '__nobody__', questionID: this.questionID });
            this.openDialog = false;
            this.$emit('close');
          },

        optionIsSelected(evento, personID,option_name){

         let opt_obj= this.optionExists(option_name);

         if(opt_obj && evento.includes(opt_obj.valor)){
            this.showTextArea[personID]=true;

           }
    
          else  this.showTextArea[personID]=false;    

         
        },


        setTextAreaContent(evento, personID,option_name, content){

          // let opt_obj= this.optionExists(option_name);

          // if(opt_obj && evento.includes(opt_obj.valor)){
            // this.showTextArea[personID]=true;
            this.reasons[personID]=content;

            // }

          // else  this.showTextArea[personID]=false;    


          },


          optionExists(option_name){

            return this.options.find(option => option.texto===option_name);

          },

          
          
          close() {

              this.openDialog = false;
              this.selected_persons = [];
              this.reasons = {};
              this.selectedReasons = {};
              this.nobodySelected = false;
              this.personToAdd = null;
              this.validationError = false;
              this.$emit('close');

            },

          clear() {
            this.$refs.form.reset();
            this.selected_persons=[];
           },

          saveSelectedPersons(){

            let  data ={};

            // VALIDACIÓN: para names_with_options se debe seleccionar al menos una persona o "Nadie"
            if (this.responseType === 'names_with_options') {
              if (this.selected_persons.filter(Boolean).length === 0 && !this.nobodySelected) {
                this.validationError = true;
                return;
              }
            }

            // VALIDACIÓN OBLIGATORIA PARA PREGUNTA 4: cada persona seleccionada debe tener al menos una razón
            const esPregunta4 = this.solarQuestion && this.solarQuestion.id_question == 4;

            if(esPregunta4 && this.selected_persons.filter(Boolean).length > 0){
              let missingReasons = [];
              this.selected_persons.filter(Boolean).forEach(person => {
                let hasReason = false;
                if (this.responseType === 'names_with_options') {
                  // Las razones están en selectedReasons como array de valores de checkboxes
                  const selected = this.selectedReasons[person.id];
                  hasReason = Array.isArray(selected) && selected.length > 0;
                } else {
                  // Las razones están en reasons como texto libre
                  const reason = this.reasons[person.id];
                  hasReason = !!reason && reason.trim().length > 0;
                }
                if (!hasReason) missingReasons.push(person.username);
              });

              if(missingReasons.length > 0){
                alert(this.$t('solar.all_reasons_required') + '\n\n' + this.$t('solar.missing_reasons_for') + ' ' + missingReasons.join(', '));
                return;
              }
            }

            // Si no hay personas seleccionadas, enviar null para eliminar todas las respuestas
            if(this.selected_persons.length === 0){
              data ={
                'selected_persons': null,
                'questionID':this.questionID
              }
            }
            else if(this.responseType=='names_with_text'){

              this.selected_persons.filter(Boolean).forEach(person=>{
                if(!(person.id in this.reasons)){
                  this.reasons[person.id]=" ";
                }
              });

              data ={
                'selected_persons':this.reasons,
                'questionID':this.questionID
              }
            }
            else if(this.responseType=='names_with_options'){
              // CHLA: guarda { personId: [valores_checkbox] }
              let checkboxData = {};
              this.selected_persons.filter(Boolean).forEach(person => {
                checkboxData[person.id] = this.selectedReasons[person.id] || [];
              });
              data = { 'selected_persons': checkboxData, 'questionID': this.questionID };
            }
            else if(this.responseType=='names_only') {
              // Para preguntas 1-3: guardamos los IDs junto con las razones de por qué les gusta
              let selected_persons_with_reasons = {};

              this.selected_persons.filter(Boolean).forEach(person=>{
                const reason = this.reasons[person.id] || "";
                selected_persons_with_reasons[person.id] = reason;
              });

              data ={
                'selected_persons':selected_persons_with_reasons,
                'questionID':this.questionID
              }

            }

            this.$emit('personsSelected', data);
            this.close();


           }

        }
    }
</script>

<style scoped>
.person-sel-card {
  border-radius: 8px !important;
  overflow: hidden;
}
.person-sel-header {
  background: #f0f4f8;
  min-height: 44px;
}
</style>

